import { APPROVED_CHANNEL_KEYS, isApprovedChannel } from '../../entitlements/channels';

export type StarterChannelState = 'connected' | 'connectable' | 'manual';

export type StarterChannelRow = {
  providerId: number;
  /** A translation key. Render with `translate(translations, providerNameKey)`. */
  providerNameKey: string;
  state: StarterChannelState;
  status: string | null;
  lastSyncAt: string | null;
  /** `Integration.type` verbatim: 'auto', 'manual', or null when the backend omits it. */
  type: string | null;
};

/**
 * Turns the live `integrations` list into the Nexoya Starter onboarding roster.
 *
 * Order is the approved list's order, not the backend's: the roster is a product statement
 * about which channels matter, and a stable order also keeps the ledger rule down the left
 * edge from reshuffling as connections land.
 *
 * A channel the tenant does not offer simply has no `Integration` row, so it is absent
 * rather than rendered as unavailable — promising a channel we cannot connect is worse than
 * not mentioning it.
 */
export function starterChannelRows(integrations: readonly any[] | null | undefined): StarterChannelRow[] {
  if (!Array.isArray(integrations)) return [];

  const approved = integrations.filter((integration) => isApprovedChannel(integration?.provider?.name));
  const order = new Map(APPROVED_CHANNEL_KEYS.map((key, index) => [key as string, index]));
  // Unknown keys (an Amazon prefix match) sort after the explicit list, stably.
  const rank = (key: string) => order.get(key) ?? APPROVED_CHANNEL_KEYS.length;

  return approved
    .slice()
    .sort((a, b) => rank(a.provider.name) - rank(b.provider.name))
    .map((integration) => ({
      providerId: integration.provider.provider_id,
      providerNameKey: integration.provider.name,
      state: stateFor(integration),
      status: integration.status ?? null,
      lastSyncAt: integration.lastSyncAt ?? null,
      type: integration.type ?? null,
    }));
}

function stateFor(integration: any): StarterChannelState {
  if (integration.connected) return 'connected';
  // `type: 'manual'` opens an email dialog rather than an OAuth popup, and an 'auto' row
  // with no connectionUrl is a silent no-op in `IntegrationCard` — both are dead ends for a
  // self-serve user, so both read as "manual" here.
  if (integration.type === 'manual' || !integration.connectionUrl) return 'manual';
  return 'connectable';
}

export function connectedCount(rows: readonly StarterChannelRow[]): number {
  return rows.filter((row) => row.state === 'connected').length;
}
